import {
  isAgentThreadToolResultContent,
  isAgentThreadToolUseContent,
  type AgentThreadRecord,
} from "./AgentThreadRecord";

/**
 * A View the agent changed on the server during a thread, through one of the
 * `create_view`, `update_view` or `delete_view` server tools.
 *
 * `viewId` is what the tool was asked to change, read from its input. A create
 * has none: the id is vended by the server and only appears in the result.
 */
export type ServerViewWrite =
  | { toolUseId: string; kind: "create" }
  | { toolUseId: string; kind: "update" | "delete"; viewId: string };

const KIND_BY_TOOL_NAME: Record<string, ServerViewWrite["kind"]> = {
  create_view: "create",
  update_view: "update",
  delete_view: "delete",
};

/**
 * Every View write in `thread` that the server reports as done.
 *
 * The browser has no other way to learn of them: the tools run on the server,
 * so unlike a picker action no mutation hook here saw the write. Only writes
 * with a `success` result count; one still running or refused changed nothing.
 * An update or delete whose input carries no readable `view_id` is left out
 * rather than reported against an unknown View.
 */
export function serverViewWritesIn(
  thread: AgentThreadRecord,
): ServerViewWrite[] {
  const succeeded = new Set<string>();
  for (const message of thread.messages) {
    for (const block of message.content) {
      if (isAgentThreadToolResultContent(block) && block.status === "success") {
        succeeded.add(block.tool_use_id);
      }
    }
  }

  const writes: ServerViewWrite[] = [];
  for (const message of thread.messages) {
    for (const block of message.content) {
      if (
        !isAgentThreadToolUseContent(block) ||
        !succeeded.has(block.tool_use_id)
      ) {
        continue;
      }
      const kind = KIND_BY_TOOL_NAME[block.tool_name];
      if (kind === undefined) {
        continue;
      }
      if (kind === "create") {
        writes.push({ toolUseId: block.tool_use_id, kind });
        continue;
      }
      const viewId = block.input?.view_id;
      if (typeof viewId === "string" && viewId.length > 0) {
        writes.push({ toolUseId: block.tool_use_id, kind, viewId });
      }
    }
  }
  return writes;
}
